<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { useTemplateRef } from 'vue';
import UiSelectOption from '@/ui/form/UiSelectOption.vue';

export interface SelectOption {
  label: string;
  value?: string | number | undefined;
  color?: string;
  subtitle?: string;
  disabled?: boolean;
  number?: number | undefined;
  isParent?: boolean;
  isChild?: boolean;
  isDelimiter?: boolean;
}

export type FilterFunction = (
  options: SelectOption[],
  searchText: string,
) => SelectOption[];

interface Props {
  error?: string;
  errors?: string[];
  label?: string;
  modelValue?: string | number | undefined | null;
  name: string;
  options: SelectOption[];
  /** Shown while nothing is selected; defaults to "Please select" */
  placeholder?: string;
  required?: boolean;
  disableSearch?: boolean;
  hint?: string;
  disabled?: boolean;
  cantBeBlank?: boolean;
  /** Custom filter function to override default filtering behavior */
  filterFunction?: FilterFunction;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  default: undefined,
  error: undefined,
  errors: () => [],
  label: undefined,
  placeholder: undefined,
  required: false,
  disableSearch: false,
  hint: undefined,
  disabled: false,
  cantBeBlank: false,
  filterFunction: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', p: string | number | undefined | null): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const picked = computed({
  get: () => props.modelValue,
  set: (value: string | number | undefined | null) => {
    if (props.modelValue === value && props.cantBeBlank) {
      return;
    }

    if (props.modelValue === value) {
      emit('update:modelValue', null);

      return;
    }

    emit('update:modelValue', value);
  },
});

const textSearch = ref('');
const uiSelect = useTemplateRef<HTMLDivElement>('uiSelect');
const isVisibleOptions = ref(false);
const openUpward = ref(false);
const selection = computed(() => {
  return props.options.find((option) => option.value === picked.value);
});

const filteredOptions = computed(() => {
  const text = textSearch.value.toLowerCase().trim();

  if (!text) {
    return props.options;
  }

  // Use custom filter function if provided
  if (props.filterFunction) {
    return props.filterFunction(props.options, text);
  }

  // Default filtering behavior
  return props.options
    .filter((option) => !option.isDelimiter)
    .filter((option) => {
      return (
        option.label.toLowerCase().includes(text) ||
        option.number?.toString().includes(text) ||
        option.subtitle?.toLowerCase().includes(text)
      );
    });
});

watch(picked, hideOptions);
onClickOutside(uiSelect, () => {
  hideOptions();
  textSearch.value = '';
});

async function selectFirstOption() {
  await toggleOptionsVisibility();

  if (filteredOptions.value.length) {
    picked.value = filteredOptions.value[0].value ?? null;
  }

  textSearch.value = '';
}

function hideOptions() {
  isVisibleOptions.value = false;
  emit('close');
}

function focusSearch() {
  const inputSearchEl = uiSelect.value?.getElementsByClassName(
    'input-search',
  )[0] as HTMLInputElement;

  if (inputSearchEl) {
    inputSearchEl.focus();
  }
}

async function toggleOptionsVisibility() {
  if (props.disabled) {
    return;
  }

  emit('open');

  isVisibleOptions.value = !isVisibleOptions.value;

  if (isVisibleOptions.value) {
    // Open the list upward when there is not enough room below for it
    // (its max-height plus margin) but more room above — otherwise a select
    // near the bottom of the viewport renders its options off-screen.
    const rect = uiSelect.value?.getBoundingClientRect();
    if (rect) {
      const neededBelow = 228; // .options max-height 220 + px(4) margin + fuzz
      const spaceBelow = window.innerHeight - rect.bottom;
      openUpward.value = spaceBelow < neededBelow && rect.top > spaceBelow;
    }

    await nextTick(focusSearch);
  }
}

function selectOption(option: SelectOption) {
  if (option.disabled) {
    return;
  }

  picked.value = option.value;
  // close explicitly — the picked watcher only fires when the parent commits
  // the new value, which it may defer (e.g. behind a confirm dialog) or skip
  hideOptions();
}

function clearSearch() {
  textSearch.value = '';
  focusSearch();
}

async function resetSelection() {
  if (props.disabled) {
    return;
  }
  picked.value = null;
}
</script>

<template>
  <UiField
    v-slot="{ fieldErrors }"
    :error="error"
    :errors="errors"
    :label="label"
    :name="name"
    :required="required"
    :hint="hint"
  >
    <div ref="uiSelect" class="ui-select">
      <!-- Input with the selected value -->
      <label
        :class="{
          'has-errors': !!fieldErrors.length,
          'no-border-bottom': disableSearch && isVisibleOptions && !openUpward,
          'is-disabled': props.disabled,
        }"
        :for="name"
        class="selection"
      >
        <template
          v-if="!isVisibleOptions || props.disableSearch || props.disabled"
        >
          <div class="select-label" @click="toggleOptionsVisibility">
            <slot v-if="selection" :selection="selection" name="selection">
              <UiSelectOption
                :label="selection.label"
                :value="selection.value"
                :subtitle="selection.subtitle"
              />
            </slot>

            <slot v-else name="placeholder">
              <span v-if="props.placeholder" class="select-placeholder">
                {{ props.placeholder }}
              </span>
              <template v-else>
                {{ $t('form.pleaseSelect') }}
              </template>
            </slot>
          </div>

          <div
            v-if="!selection || props.cantBeBlank"
            class="icon-holder"
            @click="toggleOptionsVisibility"
          >
            <UiIcon name="ChevronDown" class="icon-size-sm" />
          </div>

          <div v-else class="icon-holder" @click.stop="resetSelection">
            <UiIcon name="XIcon" class="icon-size-sm" />
          </div>
        </template>

        <template v-else>
          <div class="search-label">
            <div v-if="$slots.iconSearch" class="icon-holder">
              <slot name="iconSearch" />
            </div>

            <input
              v-model="textSearch"
              :placeholder="$t('form.typeToSearch')"
              class="input-search"
              type="text"
              @keydown.enter="selectFirstOption"
            />
          </div>

          <div class="icon-holder" @click="clearSearch">
            <UiIcon name="XIcon" class="icon-size-sm" />
          </div>
        </template>
      </label>

      <!-- Options list -->
      <transition>
        <div
          v-if="isVisibleOptions"
          class="options"
          :class="{
            'bordered-options': props.disableSearch,
            'options-up': openUpward,
          }"
        >
          <!-- Options header slot -->
          <slot name="optionsHeader" />

          <!-- Options slot -->
          <slot name="options">
            <label
              v-for="(option, index) in filteredOptions"
              :key="`${option.value}-${index}`"
              :class="{
                selected: option.value === picked,
                disabled: option.disabled && !option.isDelimiter,
                delimiter: option.isDelimiter,
                'section-delimiter': option.isDelimiter && !$slots.option,
              }"
              class="select-option"
              @click="selectOption(option)"
            >
              <!-- A consumer-provided #option slot owns rendering for every
                   row (incl. its own delimiters, e.g. the grouped/mirror
                   selects) — keep it on the original path untouched. -->
              <slot
                v-if="$slots.option"
                :option="option"
                :text-search="textSearch"
                name="option"
              />

              <!-- Default rendering: compact section divider for delimiter
                   rows (small label flanked by rules), normal option else. -->
              <span v-else-if="option.isDelimiter" class="delimiter-label">
                {{ option.label }}
              </span>
              <UiSelectOption v-else v-bind="option" />

              <UiIcon
                v-if="option.value === picked"
                name="Check"
                class="icon-size-sm"
              />
            </label>
          </slot>
        </div>
      </transition>
    </div>
  </UiField>
</template>

<style lang="scss" scoped>
.ui-select {
  position: relative;

  min-height: 3rem;
}

label.has-errors {
  border-color: var(--color-error-600);
}

.selection {
  display: flex;
  align-items: center;
  border: 1px solid #d0d5dd;
  box-shadow: var(--shadow-xs);
  background: #ffffff;
  min-height: 45px;
  border-radius: var(--border-radius-4);
  // The search field inside is borderless; the ring goes round the box.
  @include focus-ring-within;
}

.select-label {
  padding: var(--space-10) 14px;
  width: calc(100% - px(36));
  cursor: pointer;
  position: absolute;
}

// A call site's placeholder can be long (German, up to 72 characters): it stays
// on the box's one line, like the values do (UiSelectOption); the label is
// absolutely positioned and would overflow.
.select-placeholder {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.search-label {
  display: flex;
  flex-grow: 1;
  box-shadow: var(--shadow-xs);
  background: #ffffff;
  cursor: default;
  & ::placeholder {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}

.search-label input.input-search {
  width: 100%;
  flex-grow: 1;
  border: none;
  outline: none;
  margin-left: var(--space-16);
}

.options {
  position: absolute;
  z-index: 999;
  width: 100%;
  background: white;
  box-shadow: 0px 4px 4px rgba(100, 100, 100, 0.1);
  border: 1px transparent solid;
  overflow-y: scroll;
  max-height: px(220);
  border-radius: var(--border-radius-8);
  margin-top: var(--space-4);
}

// Drop-up variant: anchored above the field when the viewport has no room
// below (see toggleOptionsVisibility). bottom: 100% of the .ui-select wrapper
// places the list directly on top of the selection input.
.options.options-up {
  bottom: 100%;
  margin-top: 0;
  margin-bottom: var(--space-4);
  box-shadow: 0px -4px 4px rgba(100, 100, 100, 0.1);
}

.select-option {
  display: flex;
  justify-content: space-between;
  padding: var(--space-10) 14px;
  background: #fff;
  width: 100%;
}

.select-option:hover {
  background: var(--color-gray-50);
  box-shadow: inset 0 0 4px var(--color-gray-100);
}

.select-option.selected {
  background: var(--color-primary-50);
}
.select-option.disabled {
  cursor: not-allowed;
}
.select-option.delimiter {
  background: var(--color-gray-100);
}
// Default-path section divider (responsible-function dropdowns only): a
// compact label flanked by thin rules instead of a full-height gray row.
// Scoped to `.section-delimiter` so consumers that render their own
// delimiters via the #option slot (e.g. the mirror selects) are unaffected.
.select-option.section-delimiter {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  padding: var(--space-4) px(14);
  background: #fff;
  cursor: default;
  pointer-events: none;
}
.select-option.section-delimiter::before,
.select-option.section-delimiter::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--color-gray-200);
}
.delimiter-label {
  flex-shrink: 0;
  font-size: var(--font-size-11);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-gray-400);
  white-space: nowrap;
}

.no-border-bottom {
  border-bottom: transparent;
}

.bordered-options {
  border: 1px solid #d0d5dd;
  border-top: transparent;
  box-shadow: 0px;
}

// In drop-up mode the list sits above the field, so the open border edge
// flips: full top border, transparent bottom.
.bordered-options.options-up {
  border-top: 1px solid #d0d5dd;
  border-bottom: transparent;
}
.icon-holder {
  padding: var(--space-8);
  position: absolute;
  right: 0;
  background: #ffffff;
  margin-right: var(--space-4);
  max-width: px(40);
  color: var(--color-gray-500);
}

.v-enter-active,
.v-leave-active {
  transition: all var(--duration-slow) var(--ease-standard);
  margin-top: 0;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  margin-top: -1rem;
}

.is-disabled {
  opacity: 0.8;
  .select-label {
    cursor: not-allowed;
  }
}
</style>
